import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useMemo, useState } from "react";
import { HeaderText } from "../../shared/text/HeaderText.styled";
import FullScreenDrawerLayout from "../../shared/layout/FullScreenDrawerLayout";
import { useDrawer } from "../../context/DrawerContext";
import { useBanner } from "../../context/BannerContext";
import { useReportActions } from "../../hooks/useReportActions";
import { useDataTableActions } from "../../hooks/useDataTableActions";
import CardActionButton from "../../shared/buttons/CardActionButton";
import { useDrawerOpenReset } from "../../hooks/useDrawerOpenReset";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import { REPORT_GROUP_META } from "./groupMeta";
import { REPORT_GROUP_ORDER, ReportGroup, groupByReportGroup } from "../../../excel/reports/api/groups";
import { EdiphiReportTemplate } from "../../../excel/reports/templates/registry";
import { setCalculationAutomatic } from "../../../excel/workbook";
import { SCROLLBAR_WIDTH, thinScrollbarSx } from "../../styles/scrollbar";
import { useCanAddDataTables } from "../../hooks/useCanAddDataTables";
import {
  DATA_TABLES_LABEL,
  DATA_TABLES_LOADED,
  DATA_TABLES_LOAD_ERROR,
  MULTI_SHEET_REPORT_TOOLTIP,
  TEMPLATES_DISCOVER_ERROR,
  templateCreated,
} from "../../constants";
import { ediphiPalette } from "../../styles/colors";

interface ReportsDrawerProps {
  visibleTemplates: Record<string, EdiphiReportTemplate>;
  /** Rediscovers the workbook's reports, so a newly created one gets a card without a reload. */
  reloadReports: () => Promise<void>;
}

const ReportsDrawer = ({ visibleTemplates, reloadReports }: ReportsDrawerProps) => {
  const { isOpen, close } = useDrawer("reports");
  const { showBanner } = useBanner();
  const { withReportLogging, generateTemplateAndReport } = useReportActions();
  const { initializeDataTables } = useDataTableActions();

  /** Which item is being created, so its button alone shows the spinner. */
  const [pending, setPending] = useState<string | null>(null);
  const [filter, setFilter] = useState<ReportGroup | "all">("all");

  useDrawerOpenReset("reports", () => setFilter("all"));

  // Everything on offer, in section order, so the drawer reads like the page it opened from.
  const groups = useMemo(
    () => groupByReportGroup(Object.entries(visibleTemplates), ([, template]) => template.group),
    [visibleTemplates]
  );

  const hasOtherActions = useCanAddDataTables();

  // A filter that would show nothing is worse than no filter — with only a GCGR loaded, Estimate and
  // Contracting would both land on an empty list.
  const availableTypes = useMemo(
    () =>
      REPORT_GROUP_ORDER.filter((type) => (type === "other" ? hasOtherActions : groups.some((g) => g.type === type))),
    [groups, hasOtherActions]
  );

  // Creating a report can consume the last template of its type while the drawer is open, so the
  // selected filter is derived rather than stored — it falls back instead of stranding an empty list.
  const activeFilter = filter !== "all" && !availableTypes.includes(filter) ? "all" : filter;

  const handleCreateTemplate = async (key: string, template: EdiphiReportTemplate) => {
    setPending(key);
    let succeeded = false;
    try {
      await generateTemplateAndReport(key, template);
      showBanner(templateCreated(template.name), "success");
      succeeded = true;
    } catch (error) {
      showBanner(
        error instanceof Error && error.message ? error.message : `Failed to create ${template.name}`,
        "error"
      );
    } finally {
      // Unconditional, like the calc restore below: a run that failed part-way can still have left
      // sheets behind, and those need cards.
      await reloadReports().catch(() => showBanner(TEMPLATES_DISCOVER_ERROR, "error"));

      // Outside the success branch: generateTemplateAndReport switches the workbook to manual
      // calculation before it can fail, so a failed run must still restore it.
      await setCalculationAutomatic();
      setPending(null);
      // Only on success: a failure leaves the drawer up so the retry is one click away, matching
      // every other add path. Rediscovery above still surfaces cards for whatever landed.
      if (succeeded) close();
    }
  };

  const handleLoadDataTables = async () => {
    setPending("dataTables");
    try {
      // The button only shows while data sheets are absent, so this is always the first-load path.
      await withReportLogging(initializeDataTables, {
        context: "ReportsDrawer_handleLoadDataTables",
        message: "Failed to load data tables",
      });
      showBanner(DATA_TABLES_LOADED, "success");
      close();
    } catch {
      showBanner(DATA_TABLES_LOAD_ERROR, "error");
    } finally {
      setPending(null);
    }
  };

  const generateDrawerTitle = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box component="img" src="assets/ediphi_logo.svg" alt="" aria-hidden sx={{ width: "16px", height: "16px" }} />
      <HeaderText>Add reports</HeaderText>
    </Box>
  );

  const renderTemplateButton = (key: string, template: EdiphiReportTemplate) => (
    <CardActionButton
      key={key}
      label={template.name}
      icon={template.isMultiSheet ? LibraryBooksOutlinedIcon : undefined}
      iconTooltip={MULTI_SHEET_REPORT_TOOLTIP}
      onClick={() => void handleCreateTemplate(key, template)}
      loading={pending === key}
      disabled={pending !== null}
    />
  );

  const renderOtherActions = () => (
    <>
      {/* Needs a datasource as well as absent sheets: initializeDataTables has nothing to load without one. */}
      {hasOtherActions && (
        <CardActionButton
          label={DATA_TABLES_LABEL}
          onClick={() => void handleLoadDataTables()}
          loading={pending === "dataTables"}
          disabled={pending !== null}
        />
      )}
    </>
  );

  const renderFilteredTemplates = () => {
    // Sorted by type even without labels: ediphiReportTemplates' own order interleaves them, putting
    // Contracting SOV and the GCGR pair in the middle of the estimate reports.
    const items = groups
      .filter(({ type }) => activeFilter === "all" || activeFilter === type)
      .flatMap(({ items: entries }) => entries.map(([key, template]) => renderTemplateButton(key, template)));

    // `other` has no templates, so its actions are appended rather than filtered in.
    if (activeFilter === "all" || activeFilter === "other") {
      items.push(<React.Fragment key="other-actions">{renderOtherActions()}</React.Fragment>);
    }

    return items;
  };

  const renderFilters = () => (
    <ToggleButtonGroup
      exclusive
      // disableRipple
      size="small"
      value={activeFilter}
      onChange={(_event, next) => next && setFilter(next)}
      // Buttons share the full width and wrap to a second row rather than clipping: five full labels
      // sit close to the width of a narrow task pane.
      sx={{
        width: "100%",
        flexWrap: "wrap",
        "& .MuiToggleButton-root": {
          flex: "1 1 auto",
          fontSize: "12px",
          px: 1,
          textTransform: "none",
          backgroundColor: ediphiPalette.mainBackground,
          // Selected reads as the primary action, matching PrimaryButton rather than MUI's grey fill.
          "&.Mui-selected": {
            color: ediphiPalette.textOnAccent,
            background: ediphiPalette.brand600,
            "&:hover": { background: ediphiPalette.brand800 },
          },
          "&:hover": { color: ediphiPalette.textOnAccent, background: ediphiPalette.brand600 },
        },
      }}
    >
      <ToggleButton disableRipple value="all">
        All
      </ToggleButton>
      {availableTypes.map((type) => (
        <ToggleButton disableRipple key={type} value={type}>
          {REPORT_GROUP_META[type].label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  return (
    <FullScreenDrawerLayout isDrawerOpen={isOpen} onDrawerClose={close} drawerTitle={generateDrawerTitle()}>
      <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            p: 2,
            // The reserved track sits inside padding-right, so trim it by the track's width to keep
            // both gutters at 16px.
            pr: `${16 - SCROLLBAR_WIDTH}px`,
            flex: 1,
            minHeight: 0,
            // `scroll`, not `auto` — the trimmed padding above assumes the track always exists.
            overflowY: "scroll",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            ...thinScrollbarSx,
          }}
        >
          {renderFilters()}
          {renderFilteredTemplates()}
        </Box>
      </Box>
    </FullScreenDrawerLayout>
  );
};

export default ReportsDrawer;
